import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ModuleHeader, SpinnerWithBackground } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { EmailTemplateFormInput } from '../types'
import { AddVariables } from './AddVariables'
import { EmailPreview } from './EmailPreview'
import { EmailTemplateForm } from './EmailTemplateForm'

const defaultFormData = {
	name: '',
	subject: '',
	tags: [],
	body: '',
}
export const ManageEmailTemplate = () => {
	const { organizationId, emailTemplateId } = useParams<{
		organizationId: string
		emailTemplateId: string
	}>()
	const [isSavingEmailTemplate, setIsSavingEmailTemplate] = useState(false)
	const toast = useToast()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false)
	const [isAddVariablesOpen, setIsAddVariablesOpen] = useState(false)
	const [formData, setFormData] = useState<EmailTemplateFormInput>(defaultFormData)
	const [loadTemplate, setLoadTemplate] = useState<boolean>(false)

	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const { data: emailTemplate, isLoading: isLoadingEmailTemplate } = useQuery({
		...trpc.admin.emailTemplates.getById.queryOptions({
			id: emailTemplateId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!emailTemplateId && !!organizationId,
		refetchOnMount: true,
	})

	useEffect(() => {
		if (emailTemplate && !loadTemplate) {
			setFormData(emailTemplate)
			setLoadTemplate(true)
		}
	}, [emailTemplate, loadTemplate])

	const createEmailTemplateMutation = useMutation({
		...trpc.admin.emailTemplates.create.mutationOptions({
			onSuccess: () => {
				toast.showToast('Email template saved successfully')
				navigate(`/organizations/${organizationId}/email-templates`)
			},
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
			onSettled: () => {
				setIsSavingEmailTemplate(false)
				queryClient.invalidateQueries({
					queryKey: trpc.admin.emailTemplates.search.queryKey({ organizationId: organizationId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const editEmailTemplateMutation = useMutation({
		...trpc.admin.emailTemplates.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Email template updated successfully')
				navigate(`/organizations/${organizationId}/email-templates`)
			},
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
			onSettled: () => {
				setIsSavingEmailTemplate(false)
				queryClient.invalidateQueries({
					queryKey: trpc.admin.emailTemplates.search.queryKey({ organizationId: organizationId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleSubmit = async (data: EmailTemplateFormInput) => {
		setIsSavingEmailTemplate(true)

		if (!organizationId) return

		if (emailTemplate) {
			await editEmailTemplateMutation.mutateAsync({
				...data,
				id: emailTemplate.id,
				organizationId,
			})
		} else {
			await createEmailTemplateMutation.mutateAsync({
				...data,
				organizationId,
			})
		}
	}

	if (isLoadingOrg || isLoadingEmailTemplate || (!!emailTemplateId && !loadTemplate)) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<div className='flex gap-4 w-full'>
			{isAddVariablesOpen && (
				<main className='w-full p-6'>
					<AddVariables onClose={() => setIsAddVariablesOpen(false)} />
				</main>
			)}
			<main className='w-full p-6'>
				<div className='gap-6 flex flex-col'>
					<ModuleHeader
						breadcrumb={[
							{ label: 'Organizations', to: '/organizations' },
							{ label: organization?.name || '', to: `/organizations/${organizationId}` },
							{ label: 'Email Templates' },
						]}
						title='Create template'
						logo={{
							url: organization?.logo || '',
							name: organization?.name || '',
						}}
						buttons={[
							{
								label: 'Add variable',
								variant: 'secondary',
								onClick: () => setIsAddVariablesOpen(true),
							},
							{
								label: 'Email preview',
								variant: 'primary',
								onClick: () => setIsEmailPreviewOpen(true),
							},
						]}
					/>
				</div>
				<EmailTemplateForm
					key={loadTemplate ? emailTemplate?.id : 'new'} // Force remount form when emailTemplateId changes to reset form state
					defaultValues={emailTemplate ?? defaultFormData}
					values={formData}
					onCancel={() => navigate(`/organizations/${organizationId}/email-templates`)}
					onSubmit={handleSubmit}
					onChange={(key, value) => setFormData({ ...formData, [key]: value })}
					isSubmitting={isSavingEmailTemplate}
				/>
				<EmailPreview
					isOpen={isEmailPreviewOpen}
					onClose={() => setIsEmailPreviewOpen(false)}
					emailTemplateFormInput={formData}
				/>
			</main>
		</div>
	)
}
